import { useRef } from 'react';
import { toast } from 'sonner';
import { Button, Input, cn } from '@klaro7/ui';

import { extractErrorMessage } from '@/lib/api';

import { useUploadSeoImage } from '../hooks/useSeoConfig';

/* -------------------------------------------------------------------------- */
/*  Character counter                                                          */
/* -------------------------------------------------------------------------- */

export function CharCount({ value, ideal }: { value: string; ideal: number }) {
  const len = value.length;
  const over = len > ideal;
  return (
    <p className={cn('mt-1 text-xs', over ? 'text-amber-600' : 'text-neutral-400')}>
      {len}/{ideal} caratteri{over ? ' — oltre la lunghezza consigliata, Google potrebbe troncare' : ''}
    </p>
  );
}

/* -------------------------------------------------------------------------- */
/*  Google SERP snippet preview                                                */
/* -------------------------------------------------------------------------- */

export function GooglePreview({
  title,
  description,
  path = '',
}: {
  title: string;
  description: string;
  path?: string;
}) {
  const displayTitle = title.length > 60 ? `${title.slice(0, 60)}…` : title;
  const displayDesc = description.length > 160 ? `${description.slice(0, 160)}…` : description;
  const breadcrumb = `app.klaro7.com${path && path !== '/' ? ` › ${path.replace(/^\//, '').replace(/\//g, ' › ')}` : ''}`;

  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-4">
      <p className="mb-2 text-xs font-medium uppercase tracking-wide text-neutral-400">
        Anteprima risultato Google
      </p>
      <div className="max-w-xl font-[arial]">
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-full border border-neutral-200 bg-neutral-50">
            <img src="/images/logo-navy.png" alt="" className="h-4 w-4 object-contain" />
          </div>
          <div className="leading-tight">
            <p className="text-sm text-neutral-800">Klaro7</p>
            <p className="text-xs text-neutral-500">{breadcrumb}</p>
          </div>
        </div>
        <p className="mt-1.5 cursor-pointer text-xl leading-snug text-[#1a0dab] hover:underline">
          {displayTitle || 'Titolo della pagina'}
        </p>
        <p className="mt-1 text-sm leading-snug text-neutral-600">
          {displayDesc || 'La meta description apparirà qui.'}
        </p>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  OG image field (upload or manual URL)                                      */
/* -------------------------------------------------------------------------- */

export function OgImageField({
  label = 'Immagine di anteprima (Open Graph)',
  value,
  onChange,
}: {
  label?: string;
  value: string;
  onChange: (url: string) => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadMutation = useUploadSeoImage();

  async function handleFile(file: File | undefined) {
    if (!file) return;
    try {
      const { url } = await uploadMutation.mutateAsync(file);
      onChange(url);
      toast.success('Immagine caricata');
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  return (
    <div className="space-y-2">
      <div className="flex items-end gap-2">
        <div className="flex-1">
          <Input
            label={label}
            placeholder="https://…"
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />
        </div>
        <Button
          type="button"
          variant="secondary"
          loading={uploadMutation.isPending}
          onClick={() => fileInputRef.current?.click()}
        >
          Carica
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
      </div>
      {value && (
        <div className="flex items-center gap-3">
          <img
            src={value}
            alt="Anteprima OG"
            className="h-16 w-28 rounded-lg border border-neutral-200 object-cover"
          />
          <p className="text-xs text-neutral-500">
            Consigliato: almeno 1200×630 px per una resa ottimale nelle condivisioni.
          </p>
        </div>
      )}
    </div>
  );
}
